import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { Calculator, FileCheck, Send, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import { inrCompact } from '../../lib/format';

const FY_OPTIONS = ['2024-25', '2025-26', '2026-27'];

const SECTIONS = [
  { code: '80C', label: '80C — Investments (max ₹1.5L)', fields: ['ppf', 'lic', 'elss', 'principal_repayment', 'tuition_fees', 'others'] },
  { code: '80D', label: '80D — Medical Insurance (max ₹75K)', fields: ['self_family', 'parents', 'preventive_health'] },
  { code: '80CCD1B', label: '80CCD(1B) — NPS (max ₹50K)', fields: ['nps_self'] },
  { code: '24B', label: '24(B) — Home Loan Interest (max ₹2L)', fields: ['home_loan_interest'] },
  { code: 'HRA', label: 'HRA Exemption (Old regime only)', fields: ['rent_paid', 'metro', 'landlord_pan'] },
  { code: 'OTHER', label: 'Other (80TTA, 80G, etc.)', fields: ['savings_interest_80tta', 'donations_80g'] },
];

export default function ITDeclaration() {
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [fy, setFy] = useState('2025-26');
  const [decl, setDecl] = useState(null);
  const [tax, setTax] = useState(null);

  useEffect(() => {
    apiGet('/hr/employees').then(list => {
      setEmployees(list);
      if (list.length) setSelectedId(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    apiGet(`/hr/it-declaration/${selectedId}?fy=${fy}`).then(setDecl).catch(() => setDecl(null));
    setTax(null);
  }, [selectedId, fy]);

  const updateField = (sec, field, value) => {
    const v = field === 'landlord_pan' ? value : parseFloat(value) || 0;
    setDecl(prev => ({
      ...prev,
      sections: { ...(prev?.sections || {}), [sec]: { ...((prev?.sections || {})[sec] || {}), [field]: v } }
    }));
  };

  const save = async () => {
    try {
      await apiPost('/hr/it-declaration', { employee_id: selectedId, fy, tax_regime: decl?.tax_regime || 'New', sections: decl?.sections || {} });
      toast.success('Declaration saved');
      const updated = await apiGet(`/hr/it-declaration/${selectedId}?fy=${fy}`);
      setDecl(updated);
    } catch (err) { toast.error('Failed'); }
  };

  const submit = async () => {
    if (!decl?.exists) await save();
    try { await apiPost(`/hr/it-declaration/${selectedId}/submit?fy=${fy}`); toast.success('Submitted to HR'); apiGet(`/hr/it-declaration/${selectedId}?fy=${fy}`).then(setDecl); }
    catch (err) { toast.error('Failed'); }
  };

  const compute = async () => {
    try { const t = await apiPost(`/hr/it-declaration/${selectedId}/compute-tax?fy=${fy}`); setTax(t); }
    catch (err) { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Payroll · Investment Proof</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">IT Declaration & Tax Calculator</h1>
        <p className="text-sm text-slate-600 mt-1">Declare investments, get an instant Old vs New regime comparison.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        <aside className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <label className="label">Financial Year</label>
            <select value={fy} onChange={(e) => setFy(e.target.value)} className="input text-sm">
              {FY_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200"><div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">Employee</div></div>
            <ul className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
              {employees.map(e => (
                <li key={e.id}>
                  <button onClick={() => setSelectedId(e.id)} className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 ${selectedId === e.id ? 'bg-teal-50/60 border-l-2 border-l-[#1F7A6E]' : ''}`}>
                    <div className="font-display font-semibold text-sm text-[#0F172A] truncate">{e.name}</div>
                    <div className="text-xs text-slate-500 truncate">{e.emp_id}</div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <main className="space-y-5">
          {/* Regime + actions */}
          <div className="bg-white border border-slate-200 rounded-sm p-5 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Tax Regime</div>
              <div className="flex gap-2 mt-1">
                {['Old', 'New'].map(r => (
                  <button key={r} type="button" onClick={() => setDecl(prev => ({ ...(prev || {}), tax_regime: r }))} className={`px-4 py-1.5 rounded-sm text-sm font-display font-semibold border ${(decl?.tax_regime || 'New') === r ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'border-slate-300 text-slate-600 hover:border-[#1F7A6E]'}`} data-testid={`tax-regime-${r}`}>{r} Regime</button>
                ))}
              </div>
            </div>
            <span className={`tag ${decl?.status === 'Submitted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{decl?.status || 'Not Submitted'}</span>
            <button onClick={save} className="btn-secondary flex items-center gap-1" data-testid="save-decl-btn"><FileCheck size={14} /> Save Draft</button>
            <button onClick={compute} className="btn-secondary flex items-center gap-1" data-testid="compute-tax-btn"><Calculator size={14} /> Calculate Tax</button>
            <button onClick={submit} className="btn-primary flex items-center gap-1" data-testid="submit-decl-btn"><Send size={14} /> Submit</button>
          </div>

          {/* Tax comparison */}
          {tax && (
            <div className="bg-white border border-slate-200 rounded-sm" data-testid="tax-comparison">
              <div className="px-5 py-3 border-b border-slate-200">
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><IndianRupee size={16} /> Tax Computation · Gross Annual {inrCompact(tax.gross_annual)}</h3>
              </div>
              <div className="grid grid-cols-2 divide-x divide-slate-200">
                {[
                  { key: 'old_regime', label: 'Old Regime' },
                  { key: 'new_regime', label: 'New Regime' },
                ].map(({ key, label }) => {
                  const r = tax[key];
                  const isWinner = tax.recommended_regime === label.split(' ')[0];
                  return (
                    <div key={key} className={`p-5 ${isWinner ? 'bg-emerald-50/40' : ''}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-display font-bold text-base">{label}</div>
                        {isWinner && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-600 text-white rounded-sm">Recommended</span>}
                      </div>
                      <div className="space-y-1.5 text-sm">
                        {Object.entries(r).filter(([k]) => !['taxable_income', 'tax_payable', 'monthly_tds'].includes(k)).map(([k, v]) => (
                          <div key={k} className="flex justify-between"><span className="text-slate-600 capitalize">{k.replace(/_/g, ' ')}</span><span className="font-display font-semibold">₹{(v || 0).toLocaleString('en-IN')}</span></div>
                        ))}
                        <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between font-display font-bold"><span>Taxable Income</span><span>₹{r.taxable_income.toLocaleString('en-IN')}</span></div>
                        <div className="flex justify-between font-display font-bold text-[#1F7A6E]"><span>Annual Tax</span><span className="text-lg">₹{r.tax_payable.toLocaleString('en-IN')}</span></div>
                        <div className="flex justify-between text-xs"><span className="text-slate-500">Monthly TDS</span><span>₹{r.monthly_tds.toLocaleString('en-IN')}</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 py-3 border-t border-slate-200 bg-emerald-50 text-sm text-emerald-800">
                <strong>{tax.recommended_regime} Regime</strong> saves ~₹{tax.savings_with_recommended.toLocaleString('en-IN')} this year.
              </div>
            </div>
          )}

          {/* Sections */}
          <div className="space-y-3">
            {SECTIONS.map(sec => (
              <div key={sec.code} className="bg-white border border-slate-200 rounded-sm">
                <div className="px-5 py-3 border-b border-slate-200">
                  <h3 className="font-display font-bold text-sm">{sec.label}</h3>
                </div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-3">
                  {sec.fields.map(f => (
                    <div key={f}>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 capitalize">{f.replace(/_/g, ' ')}</label>
                      {f === 'landlord_pan' ? (
                        <input type="text" value={decl?.sections?.[sec.code]?.[f] || ''} onChange={(e) => updateField(sec.code, f, e.target.value)} className="input" placeholder="ABCDE1234F" />
                      ) : f === 'metro' ? (
                        <select value={decl?.sections?.[sec.code]?.[f] || 0} onChange={(e) => updateField(sec.code, f, e.target.value)} className="input">
                          <option value={0}>Non-Metro (40%)</option><option value={1}>Metro (50%)</option>
                        </select>
                      ) : (
                        <input type="number" min="0" value={decl?.sections?.[sec.code]?.[f] || 0} onChange={(e) => updateField(sec.code, f, e.target.value)} className="input" data-testid={`field-${sec.code}-${f}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
